import { isValidPhoneNumber } from "libphonenumber-js";
import { CountryPhoneInput } from "../../components/country-phone-input";

// Billing details Razorpay needs for the import flow (international payments
// from Indian customers). The rules below mirror the Customer and Orders API
// constraints, so a bad value is caught here instead of failing the order:
// https://razorpay.com/docs/payments/international-payments/accept-international-payments-from-indian-customers/standard-integration/build-integration/saas/

const NAME_RE = /^[A-Za-z ]{5,50}$/;
const EMAIL_RE = /^[^\s@]{1,64}@[^\s@]+\.[^\s@]+$/;
const ADDRESS_LINE_RE = /^[A-Za-z0-9 ,.\-/#&()']{3,100}$/;
const PLACE_RE = /^[A-Za-z ]{3,50}$/;
const PINCODE_RE = /^[1-9]\d{5}$/;

const clean = (value) => String(value || "").replace(/\s+/g, " ").trim();

// Same shape the profile screen uses: a dial code plus a local number.
export const buildContact = (code, number) => {
  const cleanNumber = String(number || "").replace(/[^0-9]/g, "").replace(/^0+/, "");
  const cleanCode = String(code || "").replace(/[^0-9]/g, "");
  if (!cleanNumber || !cleanCode) return "";
  return `+${cleanCode}${cleanNumber}`;
};

export const getInitialBilling = (profile = {}) => ({
  // The backend builds the Razorpay customer from the `name` column, falling
  // back to first/last name only for the prefill here.
  name:
    clean(profile.name) ||
    clean(`${profile.first_name || ""} ${profile.last_name || ""}`),
  email: clean(profile.email),
  mobile_number: String(profile.mobile_number || ""),
  mobile_code: profile.mobile_code || profile.default_mobile_code || "+91",
  mobile_country_code:
    profile.mobile_country_code || profile.default_country_code || "in",
  line1: clean(profile.postal_address1),
  line2: clean(profile.postal_address2),
  city: clean(profile.suburb_town),
  state: clean(profile.state),
  zipcode: clean(profile.postcode),
});

export const normalizeBilling = (billing) => ({
  name: clean(billing.name),
  email: clean(billing.email).toLowerCase(),
  mobile_number: String(billing.mobile_number || "").replace(/[^0-9]/g, ""),
  mobile_code: billing.mobile_code || "",
  mobile_country_code: (billing.mobile_country_code || "").toLowerCase(),
  contact: buildContact(billing.mobile_code, billing.mobile_number),
  line1: clean(billing.line1),
  line2: clean(billing.line2),
  city: clean(billing.city),
  state: clean(billing.state),
  zipcode: clean(billing.zipcode),
});

export const validateBilling = (billing) => {
  const b = normalizeBilling(billing);
  const errors = {};

  if (!NAME_RE.test(b.name)) {
    errors.name = "5–50 characters, English letters and spaces only.";
  }
  if (!EMAIL_RE.test(b.email)) {
    errors.email = "Enter a valid email address.";
  }
  if (!b.contact) {
    errors.mobile_number = "Enter your mobile number.";
  } else {
    let valid = false;
    try {
      valid = isValidPhoneNumber(b.contact);
    } catch (_e) {
      valid = false;
    }
    if (!valid) errors.mobile_number = "Enter a valid mobile number.";
  }
  if (!ADDRESS_LINE_RE.test(b.line1)) {
    errors.line1 = "3–100 characters. Letters, numbers and , . - / # & ( ) ' only.";
  }
  if (!ADDRESS_LINE_RE.test(b.line2)) {
    errors.line2 = "3–100 characters. Letters, numbers and , . - / # & ( ) ' only.";
  }
  if (!PLACE_RE.test(b.city)) {
    errors.city = "3–50 characters, English letters only.";
  }
  if (!PLACE_RE.test(b.state)) {
    errors.state = "3–50 characters, English letters only.";
  }
  if (!PINCODE_RE.test(b.zipcode)) {
    errors.zipcode = "Enter a valid 6-digit PIN code.";
  }

  return errors;
};

const FIELDS = [
  { key: "name", label: "Full name", autoComplete: "name", full: true },
  { key: "email", label: "Email", type: "email", autoComplete: "email", readOnly: true, note: "From your profile" },
  { key: "mobile_number", label: "Mobile Number", phone: true },
  { key: "line1", label: "Address line 1", autoComplete: "address-line1", placeholder: "House / flat no., building", full: true },
  { key: "line2", label: "Address line 2", autoComplete: "address-line2", placeholder: "Street, area, landmark", full: true },
  { key: "city", label: "City", autoComplete: "address-level2" },
  { key: "state", label: "State", autoComplete: "address-level1" },
  { key: "zipcode", label: "PIN code", inputMode: "numeric", autoComplete: "postal-code", maxLength: 6 },
];

const RazorpayBillingForm = ({ billing, errors, showErrors, onChange }) => {
  return (
    <div className="india-billing">
      <div className="india-billing__head">
        <h5>Billing details</h5>
        <p>
          Required by Razorpay to process your payment. These details are saved
          to your profile when you pay. Country: India.
        </p>
      </div>

      <div className="india-billing__grid">
        {FIELDS.map((field) => {
          const error = showErrors ? errors[field.key] : "";

          if (field.phone) {
            return (
              <div
                key={field.key}
                className={`india-billing__field india-billing__phone ${error ? "has-error" : ""}`}
              >
                <span>{field.label}</span>
                <CountryPhoneInput
                  value={{
                    mobile_number: billing.mobile_number || "",
                    mobile_code: billing.mobile_code || "",
                    mobile_country_code: billing.mobile_country_code || "",
                  }}
                  onChange={(next) =>
                    onChange({
                      mobile_number: next?.mobile_number || "",
                      mobile_code: next?.mobile_code || "",
                      mobile_country_code: next?.mobile_country_code || "",
                    })
                  }
                  defaultCountryCode={billing.mobile_country_code || "in"}
                  defaultMobileCode={billing.mobile_code || ""}
                  numberPlaceholder="Phone Number"
                  canShowHint={false}
                />
                {error ? <small>{error}</small> : null}
              </div>
            );
          }

          return (
            <label
              key={field.key}
              className={`india-billing__field ${field.full ? "full" : ""} ${error ? "has-error" : ""}`}
            >
              <span>{field.label}</span>
              <input
                type={field.type || "text"}
                name={field.key}
                value={billing[field.key] || ""}
                placeholder={field.placeholder}
                inputMode={field.inputMode}
                autoComplete={field.autoComplete}
                maxLength={field.maxLength}
                readOnly={field.readOnly}
                onChange={(e) => onChange(field.key, e.target.value)}
              />
              {error ? <small>{error}</small> : null}
              {!error && field.note ? <small className="note">{field.note}</small> : null}
            </label>
          );
        })}

      </div>
    </div>
  );
};

export default RazorpayBillingForm;
